import type { DashboardRouteTarget } from '@/core/interfaces/Dashboard';

type DashboardRouteAlias = {
  name: string;
  idParam?: string;
};

/**
 * Maps dashboard API / contract route aliases to registered Vue Router names.
 * Backend may send entity slugs (e.g. `phieu-nhap-kho`) or spec names
 * (e.g. `export-ticket-detail`) with `query.id` instead of route params.
 */
const DASHBOARD_ROUTE_ALIASES: Record<string, DashboardRouteAlias> = {
  'phieu-nhap-kho': {
    name: 'chi-tiet-phieu-nhap-kho',
    idParam: 'idPhieuNhapKho',
  },
  'phieu-xuat-kho': {
    name: 'chi-tiet-phieu-xuat-kho',
    idParam: 'idPhieuXuatKho',
  },
  'import-ticket': {
    name: 'chi-tiet-phieu-nhap-kho',
    idParam: 'idPhieuNhapKho',
  },
  'import-ticket-detail': {
    name: 'chi-tiet-phieu-nhap-kho',
    idParam: 'idPhieuNhapKho',
  },
  'export-ticket': {
    name: 'chi-tiet-phieu-xuat-kho',
    idParam: 'idPhieuXuatKho',
  },
  'export-ticket-detail': {
    name: 'chi-tiet-phieu-xuat-kho',
    idParam: 'idPhieuXuatKho',
  },
  'hop-dong': { name: 'chi-tiet-hop-dong', idParam: 'id' },
  kho: { name: 'chi-tiet-kho', idParam: 'id' },
  'bien-ban-giai-the': {
    name: 'chi-tiet-bien-ban-giai-the',
    idParam: 'id',
  },
  'ke-hoach-nam': { name: 'chi-tiet-ke-hoach-nam', idParam: 'id' },
  'ke-hoach-sua-chua': {
    name: 'chi-tiet-ke-hoach-sua-chua',
    idParam: 'id',
  },
  'toa-xe': { name: 'chi-tiet-toa-xe', idParam: 'id' },
  'danh-muc-toa-xe': {
    name: 'chi-tiet-danh-muc-toa-xe',
    idParam: 'id',
  },
  'danh-muc-vat-tu': {
    name: 'chi-tiet-danh-muc-vat-tu',
    idParam: 'id',
  },
  'dinh-muc-co-dinh': {
    name: 'chi-tiet-dinh-muc-co-dinh',
    idParam: 'id',
  },
  'cap-sua-chua': { name: 'chi-tiet-cap-sua-chua', idParam: 'id' },
  'vat-tu': { name: 'cap-nhat-vat-tu', idParam: 'id' },
  'inventory-list': { name: 'supplies' },
  'ticket-list': { name: 'danh-sach-phieu-nhap-kho-chua-duyet' },
  'ticket-pending': { name: 'danh-sach-phieu-nhap-kho-chua-duyet' },
  'pending-import-tickets': {
    name: 'danh-sach-phieu-nhap-kho-chua-duyet',
  },
  'pending-export-tickets': {
    name: 'danh-sach-phieu-xuat-kho-chua-duyet',
  },
};

const ROUTE_ID_PARAMS: Record<string, string> = {
  'chi-tiet-phieu-nhap-kho': 'idPhieuNhapKho',
  'chi-tiet-phieu-xuat-kho': 'idPhieuXuatKho',
};

function pickEntityId(
  params: Record<string, string | number>,
  query: Record<string, string | number>,
  idParam?: string
): string | number | undefined {
  if (idParam && params[idParam] != null) {
    return params[idParam];
  }

  if (params.id != null) {
    return params.id;
  }

  if (query.id != null) {
    return query.id;
  }

  return undefined;
}

/**
 * Resolve a dashboard deep-link into a registered Vue Router target.
 * Returns null when the target cannot be mapped safely.
 */
export function resolveDashboardRoute(
  route?: DashboardRouteTarget | null
): DashboardRouteTarget | null {
  if (!route?.name) {
    return null;
  }

  const alias = DASHBOARD_ROUTE_ALIASES[route.name];
  const resolvedName = alias?.name ?? route.name;
  const idParam = alias?.idParam ?? ROUTE_ID_PARAMS[resolvedName];

  const params: Record<string, string | number> = {
    ...(route.params ?? {}),
  };
  const query: Record<string, string | number> = {
    ...(route.query ?? {}),
  };

  if (idParam) {
    const entityId = pickEntityId(params, query, idParam);
    if (entityId != null) {
      params[idParam] = entityId;
      delete params.id;
      delete query.id;
    }
  }

  const location: DashboardRouteTarget = { name: resolvedName };

  if (Object.keys(params).length > 0) {
    location.params = params;
  }

  if (Object.keys(query).length > 0) {
    location.query = query;
  }

  return location;
}

export function getDashboardLinkProps(
  route?: DashboardRouteTarget | null
): Record<string, unknown> {
  const resolved = resolveDashboardRoute(route);
  if (!resolved) {
    return {};
  }

  return { to: resolved };
}

export function hasDashboardRoute(
  route?: DashboardRouteTarget | null
): boolean {
  return resolveDashboardRoute(route) != null;
}
